import { t } from "i18next"
import {
  DetailBar,
  Group,
  InputNumber,
  Modal,
  PriceSlider,
  RadioButtons,
  SelectInput,
  TextInput,
  Title,
} from "components/common"
import { useAddAnswersMutation, useLazyGetClientAnswerQuery, useLazyGetClientQuestionsQuery } from "features/tasks/api"
import { useEffect, useMemo } from "react"
import { useLazyGetCitiesQuery, useLazyGetCountriesQuery, useLazyGetLocalityQuery } from "features/shared/api"
import { Form } from "antd"
import getClientProfileInitialValues from "./clientProfileInitialValues"
import { localisedValue, convertToSpecificAreaUnit, requiredValidator } from "utils"
import { useSelector } from "react-redux"
import { useWatch } from "antd/lib/form/Form"

export default function ClientProfileForm({ formRef, client, allocatedTo, onSuccess }) {
  const [form] = Form.useForm()
  const { setFieldsValue, setFieldValue } = form
  const selectedCityIds = useWatch("preferredCityIds", form)
  const [getQuestions, { data: questionData, isFetching }] = useLazyGetClientQuestionsQuery()
  const [getClientAnswers, { data: answerData }] = useLazyGetClientAnswerQuery()
  const [getCities, { data: cities }] = useLazyGetCitiesQuery()
  const [getLocalities, { data: localities }] = useLazyGetLocalityQuery()
  const [getCountries, { data: countries }] = useLazyGetCountriesQuery()

  const [addAnswers, { isError, isLoading, reset, error }] = useAddAnswersMutation()

  const userCountryId = useSelector(state => state?.app?.user?.info?.AgencyConfiguration?.countryId)
  const userPlatformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)
  const getClientDetail = useMemo(
    () => [
      { label: t("Client"), value: client?.name },
      { label: t("Allocated To"), value: client?.allocationActivity?.name || client?.User?.name || allocatedTo },
    ],
    [client, allocatedTo]
  )
  const getNationalities = useMemo(() => {
    return countries?.map(country => ({
      label: localisedValue({ object: country, key: "nationalityTitle" }),
      value: country.id,
    }))
  }, [countries])

  const getOptionsFromData = (fieldData, useValue = false) => {
    const options = fieldData?.settings?.options || fieldData?.options
    return options?.map(option => ({
      label:
        localisedValue({ object: option, key: "displayTitle" }) ||
        localisedValue({ object: option, key: "name" }) ||
        option?.displayTitle ||
        option?.name,
      value: useValue ? option?.value || option?.key : option?.id || option?.value || option?.key,
    }))
  }
  const getFieldRules = fieldData => {
    if (!fieldData?.required) return []
    const fieldName = localisedValue({ object: fieldData, key: "title" }) || ""
    return [requiredValidator(fieldName)]
  }

  const onModalVisible = () => {
    getClientAnswers(client?.id)
    getQuestions(client?.id)
    getCountries()
    getCities(userCountryId, true)
  }
  const onSubmit = async values => {
    reset()
    const areaUnit = userPlatformKey === "bayut_ksa" ? "Square Meter" : "Square Feet"
    addAnswers({ values, client, areaUnit })
      .unwrap()
      .then(() => {
        if (!isError) {
          onSuccess()
          formRef?.current?.setVisible(false)
        }
      })
  }

  useEffect(() => {
    answerData && setFieldsValue(getClientProfileInitialValues(answerData))
    getCities(userCountryId, true)
    if (answerData?.preferredCityIds?.length == 1) {
      getLocalities(answerData?.preferredCityIds[0])
    }
  }, [answerData])

  // checking if section has any visible fields
  const hasPersonalInfoFields =
    questionData?.relationshipStrength ||
    questionData?.gender ||
    questionData?.nationalityId ||
    questionData?.maritalStatus ||
    questionData?.children

  const hasFinancialFields =
    questionData?.companyNameProfiling ||
    questionData?.industryId ||
    questionData?.occupation ||
    questionData?.netWorth ||
    questionData?.annualRevenueAUM ||
    questionData?.clientRatingId ||
    questionData?.keyShareholderUBO

  const hasPurchaseCriteriaFields =
    questionData?.budget ||
    questionData?.interestedTypeId ||
    questionData?.area ||
    questionData?.preferredCityIds ||
    questionData?.preferredCityRegion ||
    questionData?.hasExistingProperty ||
    questionData?.preferredLocalityId ||
    questionData?.intent ||
    questionData?.buyingIntentId ||
    questionData?.preferredFinancing

  const hasMarketExposureFields =
    questionData?.politicalExposure || questionData?.realEstateIndustryExposure || questionData?.marketExposure

  const hasInvestmentStrategyFields =
    questionData?.investmentStrategy ||
    questionData?.investmentPeriod ||
    questionData?.targetAnnualIncomePercent ||
    questionData?.targetCapitalReturnPercent ||
    questionData?.targetAllocation ||
    questionData?.capitalStructure ||
    questionData?.assetClass ||
    questionData?.riskProfile

  const hasServicesFields =
    questionData?.residencyRequirement ||
    questionData?.propertyManagement ||
    questionData?.legalTaxServices ||
    questionData?.travelLogisticsServices

  return (
    <Modal
      title={t("Fill Client Profile")}
      ref={formRef}
      okText={t("Save")}
      width={1000}
      cancelText={t("Cancel")}
      onModalVisible={onModalVisible}
      onCancel={() => {
        form.resetFields()
        formRef?.current?.setVisible(false)
        reset()
      }}
      onOk={form.submit}
      loading={isLoading}
      destroyOnClose
      footerAlert={error}
    >
      <>
        <div className="mbe-24">
          <Title level={3}>{t("Client Profile")}</Title>
          <DetailBar detailData={getClientDetail} />
        </div>

        <Form
          form={form}
          layout="vertical"
          name="client-Profile"
          initialValues={getClientProfileInitialValues(answerData)}
          onFinish={onSubmit}
          preserve={false}
          disabled={isFetching}
          scrollToFirstError
          colon={false}
        >
          {/* ============ PERSONAL INFORMATION ============ */}
          {hasPersonalInfoFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Personal Information")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.relationshipStrength && (
                  <Form.Item
                    name="relationshipStrength"
                    label={localisedValue({ object: questionData?.relationshipStrength, key: "title" })}
                    required={questionData?.relationshipStrength?.required}
                    rules={getFieldRules(questionData?.relationshipStrength)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.relationshipStrength)} />
                  </Form.Item>
                )}
                {questionData?.gender && (
                  <Form.Item
                    name="gender"
                    label={localisedValue({ object: questionData?.gender, key: "title" })}
                    required={questionData?.gender?.required}
                    rules={getFieldRules(questionData?.gender)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.gender)} />
                  </Form.Item>
                )}
                {questionData?.nationalityId && (
                  <Form.Item
                    name="nationalityId"
                    label={localisedValue({ object: questionData?.nationalityId, key: "title" })}
                    required={questionData?.nationalityId?.required}
                    rules={getFieldRules(questionData?.nationalityId)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.nationalityId, key: "placeholder" })}
                      options={getNationalities}
                      onChange={value => setFieldValue("nationalityId", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.maritalStatus && (
                  <Form.Item
                    name="maritalStatus"
                    label={localisedValue({ object: questionData?.maritalStatus, key: "title" })}
                    required={questionData?.maritalStatus?.required}
                    rules={getFieldRules(questionData?.maritalStatus)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.maritalStatus)} />
                  </Form.Item>
                )}
                {questionData?.children && (
                  <Form.Item
                    name="children"
                    label={localisedValue({ object: questionData?.children, key: "title" })}
                    required={questionData?.children?.required}
                    rules={getFieldRules(questionData?.children)}
                  >
                    <RadioButtons
                      options={[
                        { label: t("Yes"), value: "true" },
                        { label: t("No"), value: "false" },
                      ]}
                    />
                  </Form.Item>
                )}
              </Group>
            </>
          )}

          {/* ============ FINANCIAL & PROFESSIONAL DETAILS ============ */}
          {hasFinancialFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Financial & Professional Details")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.companyNameProfiling && (
                  <Form.Item
                    name="companyNameProfiling"
                    label={localisedValue({ object: questionData?.companyNameProfiling, key: "title" })}
                    required={questionData?.companyNameProfiling?.required}
                    rules={getFieldRules(questionData?.companyNameProfiling)}
                  >
                    <TextInput
                      placeholder={localisedValue({ object: questionData?.companyNameProfiling, key: "placeholder" })}
                    />
                  </Form.Item>
                )}
                {questionData?.industryId && (
                  <Form.Item
                    name="industryId"
                    label={localisedValue({ object: questionData?.industryId, key: "title" })}
                    required={questionData?.industryId?.required}
                    rules={getFieldRules(questionData?.industryId)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.industryId, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.industryId)}
                      onChange={value => setFieldValue("industryId", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.occupation && (
                  <Form.Item
                    name="occupation"
                    label={localisedValue({ object: questionData?.occupation, key: "title" })}
                    required={questionData?.occupation?.required}
                    rules={getFieldRules(questionData?.occupation)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.occupation, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.occupation, true)}
                      onChange={value => setFieldValue("occupation", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.netWorth && (
                  <Form.Item
                    name="netWorth"
                    label={localisedValue({ object: questionData?.netWorth, key: "title" })}
                    required={questionData?.netWorth?.required}
                    rules={getFieldRules(questionData?.netWorth)}
                  >
                    <PriceSlider
                      name="netWorth"
                      range={[
                        +(questionData?.netWorth?.settings?.minValue || 0),
                        +(questionData?.netWorth?.settings?.maxValue || 1000000000),
                      ]}
                      steps={100000}
                      setField={setFieldValue}
                      minFixedAtZero
                    />
                  </Form.Item>
                )}
                {questionData?.annualRevenueAUM && (
                  <Form.Item
                    name="annualRevenueAUM"
                    label={localisedValue({ object: questionData?.annualRevenueAUM, key: "title" })}
                    required={questionData?.annualRevenueAUM?.required}
                    rules={getFieldRules(questionData?.annualRevenueAUM)}
                  >
                    <PriceSlider
                      name="annualRevenueAUM"
                      range={[
                        +(questionData?.annualRevenueAUM?.settings?.minValue || 0),
                        +(questionData?.annualRevenueAUM?.settings?.maxValue || 1000000000),
                      ]}
                      steps={100000}
                      setField={setFieldValue}
                      minFixedAtZero
                    />
                  </Form.Item>
                )}
                {questionData?.clientRatingId && (
                  <Form.Item
                    name="clientRatingId"
                    label={localisedValue({ object: questionData?.clientRatingId, key: "title" })}
                    required={questionData?.clientRatingId?.required}
                    rules={getFieldRules(questionData?.clientRatingId)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.clientRatingId)} />
                  </Form.Item>
                )}
                {questionData?.keyShareholderUBO && (
                  <Form.Item
                    name="keyShareholderUBO"
                    label={localisedValue({ object: questionData?.keyShareholderUBO, key: "title" })}
                    required={questionData?.keyShareholderUBO?.required}
                    rules={getFieldRules(questionData?.keyShareholderUBO)}
                  >
                    <TextInput
                      placeholder={localisedValue({ object: questionData?.keyShareholderUBO, key: "placeholder" })}
                    />
                  </Form.Item>
                )}
              </Group>
            </>
          )}

          {/* ============ PURCHASE CRITERIA ============ */}
          {hasPurchaseCriteriaFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Purchase Criteria")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.interestedTypeId && (
                  <Form.Item
                    name="interestedTypeId"
                    label={localisedValue({ object: questionData?.interestedTypeId, key: "title" })}
                    required={questionData?.interestedTypeId?.required}
                    rules={getFieldRules(questionData?.interestedTypeId)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.interestedTypeId)} />
                  </Form.Item>
                )}
                {questionData?.area && (
                  <Form.Item
                    name="area"
                    label={localisedValue({ object: questionData?.area, key: "title" })}
                    required={questionData?.area?.required}
                    rules={getFieldRules(questionData?.area)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.area)} />
                  </Form.Item>
                )}
                {questionData?.preferredCityIds && (
                  <Form.Item
                    name="preferredCityIds"
                    label={localisedValue({ object: questionData?.preferredCityIds, key: "title" })}
                    required={questionData?.preferredCityIds?.required}
                    rules={getFieldRules(questionData?.preferredCityIds)}
                  >
                    <SelectInput
                      showSearch
                      mode="multiple"
                      placeholder={localisedValue({ object: questionData?.preferredCityIds, key: "placeholder" })}
                      options={cities}
                      onChange={value => {
                        setFieldValue("preferredCityIds", value?.length ? value : null)
                        setFieldsValue({ preferredCityRegion: null, preferredLocalityId: null })
                        if (value?.length === 1) {
                          getLocalities(value[0])
                        }
                      }}
                    />
                  </Form.Item>
                )}

                {/* Show region when 0 or 1 city, hide when multiple cities */}
                {questionData?.preferredCityRegion && (!selectedCityIds || selectedCityIds?.length === 1 ) && (
                  <Form.Item
                    name="preferredCityRegion"
                    label={localisedValue({ object: questionData?.preferredCityRegion, key: "title" })}
                    required={questionData?.preferredCityRegion?.required}
                    rules={getFieldRules(questionData?.preferredCityRegion)}
                  >
                    <RadioButtons
                      options={getOptionsFromData(questionData?.preferredCityRegion)}
                    />
                  </Form.Item>
                )}
                {/* Show locality when 0 or 1 city, hide when multiple cities */}
                {questionData?.preferredLocalityId && (!selectedCityIds || selectedCityIds?.length === 1) && localities?.length !== 0 && (
                  <Form.Item
                    name="preferredLocalityId"
                    label={localisedValue({ object: questionData?.preferredLocalityId, key: "title" })}
                    required={questionData?.preferredLocalityId?.required}
                    rules={getFieldRules(questionData?.preferredLocalityId)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.preferredLocalityId, key: "placeholder" })}
                      options={localities}
                      onChange={value => setFieldValue("preferredLocalityId", value)}
                      allowClear
                    />
                  </Form.Item>
                )}

                {questionData?.hasExistingProperty && (
                  <Form.Item
                    name="hasExistingProperty"
                    label={localisedValue({ object: questionData?.hasExistingProperty, key: "title" })}
                    required={questionData?.hasExistingProperty?.required}
                    rules={getFieldRules(questionData?.hasExistingProperty)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.hasExistingProperty)} />
                  </Form.Item>
                )}

                {questionData?.intent && (
                  <Form.Item
                    name="intent"
                    label={localisedValue({ object: questionData?.intent, key: "title" })}
                    required={questionData?.intent?.required}
                    rules={getFieldRules(questionData?.intent)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.intent)} />
                  </Form.Item>
                )}
                {questionData?.buyingIntentId && (
                  <Form.Item
                    name="buyingIntentId"
                    label={localisedValue({ object: questionData?.buyingIntentId, key: "title" })}
                    required={questionData?.buyingIntentId?.required}
                    rules={getFieldRules(questionData?.buyingIntentId)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.buyingIntentId)} />
                  </Form.Item>
                )}
                {questionData?.preferredFinancing && (
                  <Form.Item
                    name="preferredFinancing"
                    label={localisedValue({ object: questionData?.preferredFinancing, key: "title" })}
                    required={questionData?.preferredFinancing?.required}
                    rules={getFieldRules(questionData?.preferredFinancing)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.preferredFinancing)} />
                  </Form.Item>
                )}
              </Group>
              {questionData?.budget && (
                <Form.Item
                  name="budget"
                  label={localisedValue({ object: questionData?.budget, key: "title" })}
                  required={questionData?.budget?.required}
                  rules={getFieldRules(questionData?.budget)}
                >
                  <PriceSlider
                    name="budget"
                    range={[
                      +questionData?.budget?.options?.[0]?.minValue,
                      +questionData?.budget?.options?.[0]?.maxValue,
                    ]}
                    steps={100000}
                    setField={setFieldValue}
                    minFixedAtZero
                  />
                </Form.Item>
              )}
            </>
          )}

          {/* ============ MARKET EXPOSURE ============ */}
          {hasMarketExposureFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Market Exposure")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.politicalExposure && (
                  <Form.Item
                    name="politicalExposure"
                    label={localisedValue({ object: questionData?.politicalExposure, key: "title" })}
                    required={questionData?.politicalExposure?.required}
                    rules={getFieldRules(questionData?.politicalExposure)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.politicalExposure)} />
                  </Form.Item>
                )}
                {questionData?.realEstateIndustryExposure && (
                  <Form.Item
                    name="realEstateIndustryExposure"
                    label={localisedValue({ object: questionData?.realEstateIndustryExposure, key: "title" })}
                    required={questionData?.realEstateIndustryExposure?.required}
                    rules={getFieldRules(questionData?.realEstateIndustryExposure)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.realEstateIndustryExposure)} />
                  </Form.Item>
                )}
                {questionData?.marketExposure && (
                  <Form.Item
                    name="marketExposure"
                    label={localisedValue({ object: questionData?.marketExposure, key: "title" })}
                    required={questionData?.marketExposure?.required}
                    rules={getFieldRules(questionData?.marketExposure)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.marketExposure)} />
                  </Form.Item>
                )}
              </Group>
            </>
          )}

          {/* ============ INVESTMENT STRATEGY ============ */}
          {hasInvestmentStrategyFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Investment Strategy")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.investmentStrategy && (
                  <Form.Item
                    name="investmentStrategy"
                    label={localisedValue({ object: questionData?.investmentStrategy, key: "title" })}
                    required={questionData?.investmentStrategy?.required}
                    rules={getFieldRules(questionData?.investmentStrategy)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.investmentStrategy, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.investmentStrategy)}
                      onChange={value => setFieldValue("investmentStrategy", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.investmentPeriod && (
                  <Form.Item
                    name="investmentPeriod"
                    label={localisedValue({ object: questionData?.investmentPeriod, key: "title" })}
                    required={questionData?.investmentPeriod?.required}
                    rules={getFieldRules(questionData?.investmentPeriod)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.investmentPeriod)} />
                  </Form.Item>
                )}
                {questionData?.targetAnnualIncomePercent && (
                  <Form.Item
                    name="targetAnnualIncomePercent"
                    label={localisedValue({ object: questionData?.targetAnnualIncomePercent, key: "title" })}
                    required={questionData?.targetAnnualIncomePercent?.required}
                    rules={getFieldRules(questionData?.targetAnnualIncomePercent)}
                  >
                    <InputNumber
                      addonAfter="%"
                      max={100}
                      min={0}
                      controls={false}
                      className="w-100"
                      precision={0}
                      placeholder={localisedValue({
                        object: questionData?.targetAnnualIncomePercent,
                        key: "placeholder",
                      })}
                    />
                  </Form.Item>
                )}
                {questionData?.targetCapitalReturnPercent && (
                  <Form.Item
                    name="targetCapitalReturnPercent"
                    label={localisedValue({ object: questionData?.targetCapitalReturnPercent, key: "title" })}
                    required={questionData?.targetCapitalReturnPercent?.required}
                    rules={getFieldRules(questionData?.targetCapitalReturnPercent)}
                  >
                    <InputNumber
                      addonAfter="%"
                      max={100}
                      min={0}
                      controls={false}
                      className="w-100"
                      precision={0}
                      placeholder={localisedValue({
                        object: questionData?.targetCapitalReturnPercent,
                        key: "placeholder",
                      })}
                    />
                  </Form.Item>
                )}
                {questionData?.targetAllocation && (
                  <Form.Item
                    name="targetAllocation"
                    label={localisedValue({ object: questionData?.targetAllocation, key: "title" })}
                    required={questionData?.targetAllocation?.required}
                    rules={getFieldRules(questionData?.targetAllocation)}
                  >
                    <PriceSlider
                      name="targetAllocation"
                      range={[
                        +(questionData?.targetAllocation?.settings?.minValue || 0),
                        +(questionData?.targetAllocation?.settings?.maxValue || 1000000000),
                      ]}
                      steps={100000}
                      setField={setFieldValue}
                      minFixedAtZero
                    />
                  </Form.Item>
                )}
                {questionData?.capitalStructure && (
                  <Form.Item
                    name="capitalStructure"
                    label={localisedValue({ object: questionData?.capitalStructure, key: "title" })}
                    required={questionData?.capitalStructure?.required}
                    rules={getFieldRules(questionData?.capitalStructure)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.capitalStructure)} />
                  </Form.Item>
                )}
                {questionData?.assetClass && (
                  <Form.Item
                    name="assetClass"
                    label={localisedValue({ object: questionData?.assetClass, key: "title" })}
                    required={questionData?.assetClass?.required}
                    rules={getFieldRules(questionData?.assetClass)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.assetClass, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.assetClass)}
                      onChange={value => setFieldValue("assetClass", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.riskProfile && (
                  <Form.Item
                    name="riskProfile"
                    label={localisedValue({ object: questionData?.riskProfile, key: "title" })}
                    required={questionData?.riskProfile?.required}
                    rules={getFieldRules(questionData?.riskProfile)}
                  >
                    <SelectInput
                      showSearch
                      placeholder={localisedValue({ object: questionData?.riskProfile, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.riskProfile)}
                      onChange={value => setFieldValue("riskProfile", value)}
                    />
                  </Form.Item>
                )}
              </Group>
            </>
          )}

          {/* ============ SERVICES REQUIRED ============ */}
          {hasServicesFields && (
            <>
              <div className="mbe-24">
                <Title level={4}>{t("Services Required")}</Title>
              </div>
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                {questionData?.residencyRequirement && (
                  <Form.Item
                    name="residencyRequirement"
                    label={localisedValue({ object: questionData?.residencyRequirement, key: "title" })}
                    required={questionData?.residencyRequirement?.required}
                    rules={getFieldRules(questionData?.residencyRequirement)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.residencyRequirement)} />
                  </Form.Item>
                )}
                {questionData?.propertyManagement && (
                  <Form.Item
                    name="propertyManagement"
                    label={localisedValue({ object: questionData?.propertyManagement, key: "title" })}
                    required={questionData?.propertyManagement?.required}
                    rules={getFieldRules(questionData?.propertyManagement)}
                  >
                    <RadioButtons options={getOptionsFromData(questionData?.propertyManagement)} />
                  </Form.Item>
                )}
                {questionData?.legalTaxServices && (
                  <Form.Item
                    name="legalTaxServices"
                    label={localisedValue({ object: questionData?.legalTaxServices, key: "title" })}
                    required={questionData?.legalTaxServices?.required}
                    rules={getFieldRules(questionData?.legalTaxServices)}
                  >
                    <SelectInput
                      mode="multiple"
                      placeholder={localisedValue({ object: questionData?.legalTaxServices, key: "placeholder" })}
                      options={getOptionsFromData(questionData?.legalTaxServices)}
                      onChange={value => setFieldValue("legalTaxServices", value)}
                    />
                  </Form.Item>
                )}
                {questionData?.travelLogisticsServices && (
                  <Form.Item
                    name="travelLogisticsServices"
                    label={localisedValue({ object: questionData?.travelLogisticsServices, key: "title" })}
                    required={questionData?.travelLogisticsServices?.required}
                    rules={getFieldRules(questionData?.travelLogisticsServices)}
                  >
                    <SelectInput
                      mode="multiple"
                      placeholder={localisedValue({
                        object: questionData?.travelLogisticsServices,
                        key: "placeholder",
                      })}
                      options={getOptionsFromData(questionData?.travelLogisticsServices)}
                      onChange={value => setFieldValue("travelLogisticsServices", value)}
                    />
                  </Form.Item>
                )}
              </Group>
            </>
          )}
        </Form>
      </>
    </Modal>
  )
}
